import type {
  ChangeLedgerRow,
  DemandCoverage,
  LedgerDemand,
  LedgerReason,
  LedgerVerdict,
  SearchImpactDescriptor,
} from '~/features/catalog-operations/types/search-impact.types';

/** Compact monthly-volume figure: 27100 → "27.1K", 720 → "720". */
export function formatVolume(n: number): string {
  const abs = Math.abs(n);
  if (abs < 1000) return String(abs);
  return `${(abs / 1000).toFixed(abs % 1000 === 0 ? 0 : 1).replace(/\.0$/, '')}K`;
}

/** Fields a descriptor actually reached vs. the fields that could carry it. */
export function descriptorCoverage(descriptor: SearchImpactDescriptor): {
  hit: number;
  total: number;
} {
  const applicable = Object.values(descriptor.placement).filter((p) => p !== 'not-applicable');
  return { hit: applicable.filter((p) => p === 'placed').length, total: applicable.length };
}

/* ── Change ledger ─────────────────────────────────────────────────────────── */

/** Tag text per verdict, as the design names them. */
export const VERDICT_LABEL: Record<LedgerVerdict, string> = {
  gained: 'Gained',
  renamed: 'Renamed',
  restated: 'Merged',
  narrowed: 'Narrowed',
  unsupported: 'Unverified',
  lost: 'Removed',
};

/** Tag + dot colours per verdict, all from design tokens. */
export const VERDICT_STYLE: Record<LedgerVerdict, { tag: string; dot: string }> = {
  gained: { tag: 'bg-success-50 text-success', dot: 'bg-success' },
  renamed: { tag: 'bg-primary/10 text-primary', dot: 'bg-primary' },
  restated: { tag: 'bg-replaced-50 text-replaced', dot: 'bg-replaced' },
  narrowed: { tag: 'bg-warning-50 text-warning', dot: 'bg-warning' },
  unsupported: { tag: 'bg-warning-50 text-warning', dot: 'bg-warning' },
  lost: { tag: 'bg-error-50 text-error', dot: 'bg-error' },
};

/** Filter chips: the design groups renames with merges, and removals with unverified values. */
export type LedgerFilter = 'all' | 'gained' | 'moved' | 'narrowed' | 'removed';

export const LEDGER_FILTERS: { key: LedgerFilter; label: string; dot: string | null }[] = [
  { key: 'all', label: 'All', dot: null },
  { key: 'gained', label: 'Gained', dot: VERDICT_STYLE.gained.dot },
  { key: 'moved', label: 'Renamed or merged', dot: VERDICT_STYLE.renamed.dot },
  { key: 'narrowed', label: 'Narrowed', dot: VERDICT_STYLE.narrowed.dot },
  { key: 'removed', label: 'Removed', dot: VERDICT_STYLE.lost.dot },
];

/** Whether a row belongs under a filter chip. */
export function inLedgerFilter(row: ChangeLedgerRow, filter: LedgerFilter): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'moved':
      return row.verdict === 'renamed' || row.verdict === 'restated';
    case 'removed':
      return row.verdict === 'lost' || row.verdict === 'unsupported';
    default:
      return row.verdict === filter;
  }
}

const VERDICT_ORDER: Record<LedgerVerdict, number> = {
  gained: 0,
  narrowed: 1,
  renamed: 2,
  restated: 3,
  unsupported: 4,
  lost: 5,
};

/** Biggest volume change first, then by what happened — the design's sort. */
export function sortLedger(rows: ChangeLedgerRow[]): ChangeLedgerRow[] {
  return rows
    .slice()
    .sort(
      (a, b) =>
        b.volumeChange - a.volumeChange || VERDICT_ORDER[a.verdict] - VERDICT_ORDER[b.verdict],
    );
}

/** Reason category (small caps line) and label per fixed reason code. */
export const REASON_COPY: Record<
  LedgerReason,
  { category: string; label: string; tone: 'new' | 'changed' | 'review' | 'removed' }
> = {
  in_source: {
    category: 'New information',
    label: 'Already in source copy, now structured',
    tone: 'new',
  },
  derived: { category: 'New information', label: 'Derived from input data', tone: 'new' },
  reworded: { category: 'New information', label: 'Reworded', tone: 'new' },
  key_renamed: { category: 'Changed', label: 'Key renamed', tone: 'changed' },
  duplicate_merged: { category: 'Changed', label: 'Duplicate merged', tone: 'changed' },
  value_split: { category: 'Changed', label: 'Value split', tone: 'changed' },
  no_evidence_found: {
    category: 'Needs review',
    label: 'No evidence found in input',
    tone: 'review',
  },
  dropped_in_enrichment: { category: 'Removed', label: 'Removed in enrichment', tone: 'removed' },
};

export const REASON_TONE_STYLE: Record<'new' | 'changed' | 'review' | 'removed', string> = {
  new: 'bg-success-50 text-success',
  changed: 'bg-primary/10 text-primary',
  review: 'bg-warning-50 text-warning',
  removed: 'bg-muted text-muted-foreground',
};

/* ── Demand ("Serves") ─────────────────────────────────────────────────────── */

/** Opportunity label: ≥10K/mo defend; competition index under 34 win; else long tail. */
export function opportunityOf(d: LedgerDemand): { label: string; className: string } {
  // Unknown volume can't qualify as high-volume; fall through to competition / long tail.
  if (d.monthlyVolume !== null && d.monthlyVolume >= 10_000) {
    return { label: 'High volume, defend', className: 'bg-primary/10 text-primary' };
  }
  if (d.competitionIndex !== null && d.competitionIndex < 34) {
    return { label: 'Low competition, win', className: 'bg-success-50 text-success' };
  }
  return { label: 'Long tail, cover cheaply', className: 'bg-muted text-muted-foreground' };
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** Trend label from YoY change or peak month; null when there's no history to judge. */
export function trendOf(d: LedgerDemand): { label: string; className: string } | null {
  if (d.yoyPct !== null && d.yoyPct >= 15) {
    return {
      label: `Rising +${String(Math.round(d.yoyPct))}% YoY`,
      className: 'bg-success-50 text-success',
    };
  }
  if (d.yoyPct !== null && d.yoyPct <= -15) {
    return {
      label: `Falling ${String(Math.abs(Math.round(d.yoyPct)))}% YoY`,
      className: 'bg-warning-50 text-warning',
    };
  }
  if (d.peakMonth !== null && d.peakMonth >= 1 && d.peakMonth <= 12) {
    return {
      label: `Peaks in ${MONTHS[d.peakMonth - 1] ?? ''}`,
      className: 'bg-replaced-50 text-replaced',
    };
  }
  if (d.yoyPct === null) return null;
  return { label: 'Steady YoY', className: 'bg-muted text-muted-foreground' };
}

/** Commercial intent: a top-of-page bid of $2.00 or more. */
export function hasCommercialIntent(d: LedgerDemand): boolean {
  return d.bidHigh !== null && d.bidHigh >= 2;
}

export const COVERAGE_COPY: Record<DemandCoverage, { label: string; className: string }> = {
  new: { label: 'New coverage', className: 'bg-success-50 text-success' },
  had: { label: 'Already covered', className: 'bg-muted text-muted-foreground' },
  lost: { label: 'No longer covered', className: 'bg-error-50 text-error' },
};
